/* eslint-disable no-unused-vars */
import { useEffect, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../Services/FirebaseService';
import NotificationRepository, {
    NotificationFilters,
    NotificationItem,
    NotificationPage,
} from '../Repositories/NotificationRepository';
import { notificationFiltersKey, sortNotificationCategories } from '../Utils/notificationGroups';

// Taille d'une page du panneau (les 20 dernières, puis « Voir plus »)
export const NOTIFICATIONS_PAGE_SIZE = 20;
// Rythme normal du polling des compteurs
const POLL_INTERVAL_MS = 60 * 1000;
// Rythme ralenti après MAX_CONSECUTIVE_ERRORS échecs d'affilée (API absente, migration non passée...)
const BACKOFF_INTERVAL_MS = 5 * 60 * 1000;
const MAX_CONSECUTIVE_ERRORS = 3;
// Limiteur de fréquence : deux appels identiques sont espacés d'au moins 6 s (au-delà des 5 s
// de mémorisation d'ApiService, pour toujours obtenir une réponse fraîche du serveur).
const MIN_CALL_GAP_MS = 6 * 1000;
// Âge au-delà duquel la liste est rechargée à l'ouverture du panneau
const LIST_STALE_MS = 2 * 60 * 1000;
// Juste après « vu », un compteur serveur calculé avant l'enregistrement du repère est ignoré
const SEEN_GRACE_MS = MIN_CALL_GAP_MS;

export const DEFAULT_NOTIFICATION_FILTERS: NotificationFilters = { category: null, unreadOnly: false };

export interface NotificationsState {
    /** Compte propriétaire des données ("teachr:12", "client:34"). */
    userKey: string | null;
    /** Page(s) chargée(s) pour les filtres courants. */
    items: NotificationItem[];
    /** Total pour les filtres courants. */
    total: number;
    /** Non lues, toutes catégories (sous-titre du panneau). */
    unreadCount: number;
    /** Non lues arrivées depuis la dernière ouverture du panneau (badge de la cloche). */
    newCount: number;
    /** Catégories rencontrées dans les listes reçues, pour les puces de filtre. */
    categories: string[];
    /** Filtres appliqués à items et total. */
    filters: NotificationFilters;
    /** Dernière page chargée (1 = les plus récentes). */
    page: number;
    /** Première page reçue au moins une fois (pour les filtres courants). */
    loaded: boolean;
    loading: boolean;
    loadingMore: boolean;
    /** Échecs répétés : API considérée indisponible, polling en back-off. */
    unavailable: boolean;
}

export interface UseNotificationsResult extends NotificationsState {
    /** Utilisateur connecté (prof ou client) : sinon aucun appel n'est fait. */
    enabled: boolean;
    hasMore: boolean;
    /** Recharge la première page si elle est absente ou périmée (ouverture du panneau). */
    refresh: () => void;
    loadMore: () => void;
    /** Marque comme lue de façon optimiste, rétablit l'état en cas d'échec. */
    markAsRead: (notification: NotificationItem) => void;
    markAllAsRead: () => void;
    /** Ouverture du panneau : badge à zéro tout de suite, repère « vu » enregistré côté serveur. */
    markSeen: () => void;
    /** Masque une notification de façon optimiste, la rétablit en cas d'échec. */
    hide: (notification: NotificationItem) => void;
    /** Change les filtres et recharge la première page. */
    setFilters: (patch: Partial<NotificationFilters>) => void;
}

const INITIAL_STATE: NotificationsState = {
    userKey: null,
    items: [],
    total: 0,
    unreadCount: 0,
    newCount: 0,
    categories: [],
    filters: DEFAULT_NOTIFICATION_FILTERS,
    page: 0,
    loaded: false,
    loading: false,
    loadingMore: false,
    unavailable: false,
};

/*
 * Store de module partagé par toutes les cloches montées (desktop et mobile), le widget du
 * tableau de bord et la page, conservé d'une page à l'autre : un seul polling, pas de
 * rechargement à chaque navigation, rien de persisté.
 * Tous les échecs sont silencieux (pas de Swal, pas de log) : l'API peut répondre 404 ou 500
 * tant que la migration n'est pas passée.
 */
let state: NotificationsState = INITIAL_STATE;
const listeners = new Set<() => void>();
let consumers = 0;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
// Incrémenté à chaque changement de compte : les réponses destinées au compte précédent sont ignorées
let generation = 0;
// Incrémenté à chaque lecture locale ou « vu » : un compteur demandé avant est ignoré
let mutationVersion = 0;
// Incrémenté à chaque changement de filtres : une liste demandée avec d'autres filtres est ignorée
let filtersVersion = 0;
let consecutiveErrors = 0;
let lastPollAt = 0;
let listFetchedAt = 0;
// Dernier « vu » local : les compteurs serveur reçus juste après gardent le badge à zéro
let lastSeenAt = 0;
// Le compteur serveur a bougé depuis le dernier chargement de la liste
let listStale = false;
const lastCallAt = new Map<string, number>();
// Lectures faites dans cette session, réappliquées sur toute liste reçue ensuite
const readIds = new Set<number>();
let readAllUpToId = 0;
// Masquages faits dans cette session, retirés de toute liste reçue ensuite
const hiddenIds = new Set<number>();

function emit() {
    listeners.forEach((listener) => listener());
}

function setState(patch: Partial<NotificationsState>) {
    state = { ...state, ...patch };
    emit();
}

function subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

function getSnapshot() {
    return state;
}

function canCall(key: string, gap: number = MIN_CALL_GAP_MS) {
    return Date.now() - (lastCallAt.get(key) ?? 0) >= gap;
}

function markCall(key: string) {
    lastCallAt.set(key, Date.now());
}

function isInBackoff() {
    return consecutiveErrors >= MAX_CONSECUTIVE_ERRORS;
}

function nextPollDelay() {
    return isInBackoff() ? BACKOFF_INTERVAL_MS : POLL_INTERVAL_MS;
}

function recordSuccess() {
    consecutiveErrors = 0;
    if (state.unavailable) setState({ unavailable: false });
}

function recordFailure() {
    consecutiveErrors += 1;
    if (isInBackoff() && !state.unavailable) setState({ unavailable: true });
}

function seenRecently() {
    return lastSeenAt !== 0 && Date.now() - lastSeenAt < SEEN_GRACE_MS;
}

// Compteur « nouvelles » à retenir d'une réponse serveur : 0 juste après un « vu » local
function serverNewCount(data: { newCount?: unknown }) {
    if (seenRecently()) return 0;
    return typeof data.newCount === 'number' ? data.newCount : 0;
}

function withLocalChanges(items: NotificationItem[]) {
    return items
        .filter((item) => !hiddenIds.has(item.id))
        .map((item) => (
            !item.isRead && (readIds.has(item.id) || item.id <= readAllUpToId) ? { ...item, isRead: true } : item
        ));
}

function mergeCategories(items: NotificationItem[]) {
    return sortNotificationCategories([...state.categories, ...items.map((item) => item.category)]);
}

function isNotificationPage(data: any): data is NotificationPage {
    return !!data && Array.isArray(data.items) && typeof data.unreadCount === 'number';
}

function listKey(page: number, filters: NotificationFilters) {
    return `list:${page}:${notificationFiltersKey(filters)}`;
}

async function fetchFirstPage(force: boolean = false) {
    if (!state.userKey) return;
    const filters = state.filters;
    const key = listKey(1, filters);
    if (!force && !canCall(key)) return;
    const currentGeneration = generation;
    const currentVersion = mutationVersion;
    const currentFilters = filtersVersion;
    markCall(key);
    lastPollAt = Date.now();
    setState({ loading: true });
    try {
        const data = await NotificationRepository.getNotifications(1, NOTIFICATIONS_PAGE_SIZE, filters);
        if (currentGeneration !== generation) return;
        if (!isNotificationPage(data)) throw new Error('Réponse inattendue');
        recordSuccess();
        // les filtres ont changé entre-temps : cette page ne correspond plus, seuls les compteurs servent
        if (currentFilters !== filtersVersion) {
            if (currentVersion === mutationVersion) {
                setState({ unreadCount: data.unreadCount, newCount: serverNewCount(data), categories: mergeCategories(data.items) });
            }
            return;
        }
        listFetchedAt = Date.now();
        listStale = false;
        const items = withLocalChanges(data.items);
        setState({
            items,
            total: typeof data.total === 'number' ? Math.max(data.total - (data.items.length - items.length), 0) : items.length,
            unreadCount: currentVersion === mutationVersion ? data.unreadCount : state.unreadCount,
            newCount: currentVersion === mutationVersion ? serverNewCount(data) : state.newCount,
            categories: mergeCategories(data.items),
            page: 1,
            loaded: true,
            loading: false,
        });
    } catch {
        if (currentGeneration !== generation || currentFilters !== filtersVersion) return;
        recordFailure();
        setState({ loading: false });
    }
}

async function fetchUnreadCount() {
    if (!state.userKey || !canCall('count')) return;
    const currentGeneration = generation;
    const currentVersion = mutationVersion;
    markCall('count');
    lastPollAt = Date.now();
    try {
        const data = await NotificationRepository.getUnreadCount();
        if (currentGeneration !== generation) return;
        if (typeof data?.unreadCount !== 'number') throw new Error('Réponse inattendue');
        recordSuccess();
        if (currentVersion !== mutationVersion) return;
        const newCount = serverNewCount(data);
        if (data.unreadCount === state.unreadCount && newCount === state.newCount) return;
        // nouvelle notification ou lecture depuis un autre appareil : la liste sera rechargée à l'ouverture
        listStale = true;
        setState({ unreadCount: data.unreadCount, newCount });
    } catch {
        if (currentGeneration === generation) recordFailure();
    }
}

function clearPoll() {
    if (pollTimer !== null) {
        clearTimeout(pollTimer);
        pollTimer = null;
    }
}

function schedulePoll(delay: number) {
    clearPoll();
    if (consumers === 0 || !state.userKey || document.visibilityState !== 'visible') return;
    pollTimer = setTimeout(runPoll, Math.max(delay, 0));
}

async function runPoll() {
    pollTimer = null;
    const currentGeneration = generation;
    // tant que la première page n'est pas arrivée, c'est elle qu'on retente (elle porte aussi les compteurs)
    await (state.loaded ? fetchUnreadCount() : fetchFirstPage());
    if (currentGeneration === generation) schedulePoll(nextPollDelay());
}

function handleVisibilityChange() {
    if (document.visibilityState !== 'visible') {
        // onglet en arrière-plan : plus aucun appel
        clearPoll();
        return;
    }
    // retour au premier plan : rafraîchissement immédiat, sauf en back-off ou juste après un appel
    schedulePoll(lastPollAt + (isInBackoff() ? BACKOFF_INTERVAL_MS : MIN_CALL_GAP_MS) - Date.now());
}

function startPolling() {
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // premier montage : appel immédiat ; remontage après navigation : on reprend le rythme en cours
    schedulePoll(lastPollAt === 0 ? 0 : lastPollAt + nextPollDelay() - Date.now());
}

function stopPolling() {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    clearPoll();
}

// lastCallAt est conservé : il ne sert qu'à limiter la fréquence (le cache d'ApiService est propre à chaque compte)
function resetStore(userKey: string) {
    generation += 1;
    filtersVersion += 1;
    clearPoll();
    consecutiveErrors = 0;
    lastPollAt = 0;
    listFetchedAt = 0;
    lastSeenAt = 0;
    listStale = false;
    readIds.clear();
    hiddenIds.clear();
    readAllUpToId = 0;
    state = { ...INITIAL_STATE, userKey };
    emit();
}

function activate(userKey: string) {
    const userChanged = state.userKey !== userKey;
    if (userChanged) resetStore(userKey);
    consumers += 1;
    if (consumers === 1) {
        startPolling();
    } else if (userChanged) {
        schedulePoll(0);
    }
}

function deactivate() {
    consumers = Math.max(consumers - 1, 0);
    if (consumers === 0) stopPolling();
}

function refreshNotifications() {
    if (!state.userKey || state.loading) return;
    const isFresh = state.loaded && !listStale && Date.now() - listFetchedAt < LIST_STALE_MS;
    if (isFresh) return;
    // en back-off, l'ouverture du panneau relance l'API au plus une fois par minute
    if (isInBackoff() && !canCall(listKey(1, state.filters), POLL_INTERVAL_MS)) return;
    fetchFirstPage();
}

async function loadMoreNotifications() {
    if (!state.userKey || !state.loaded || state.loadingMore || state.items.length >= state.total) return;
    const filters = state.filters;
    const nextPage = state.page + 1;
    const key = listKey(nextPage, filters);
    if (!canCall(key)) return;
    const currentGeneration = generation;
    const currentFilters = filtersVersion;
    markCall(key);
    setState({ loadingMore: true });
    try {
        const data = await NotificationRepository.getNotifications(nextPage, NOTIFICATIONS_PAGE_SIZE, filters);
        if (currentGeneration !== generation || currentFilters !== filtersVersion) return;
        if (!isNotificationPage(data)) throw new Error('Réponse inattendue');
        // des notifications arrivées entre-temps décalent les pages : on écarte les doublons
        const knownIds = new Set(state.items.map((item) => item.id));
        const fresh = withLocalChanges(data.items).filter((item) => !knownIds.has(item.id));
        setState({
            items: [...state.items, ...fresh],
            // page vide : plus rien à charger, on masque « Voir plus »
            total: data.items.length === 0 || typeof data.total !== 'number' ? state.items.length + fresh.length : data.total,
            categories: mergeCategories(data.items),
            page: nextPage,
            loadingMore: false,
        });
    } catch {
        if (currentGeneration === generation && currentFilters === filtersVersion) setState({ loadingMore: false });
    }
}

async function markNotificationAsRead(notification: NotificationItem) {
    if (!state.userKey || notification.isRead || readIds.has(notification.id)) return;
    const key = `read:${notification.id}`;
    if (!canCall(key)) return;
    const currentGeneration = generation;
    markCall(key);
    readIds.add(notification.id);
    mutationVersion += 1;
    setState({
        items: state.items.map((item) => (item.id === notification.id ? { ...item, isRead: true } : item)),
        unreadCount: Math.max(state.unreadCount - 1, 0),
    });
    try {
        await NotificationRepository.markAsRead(notification.id);
    } catch {
        if (currentGeneration !== generation) return;
        readIds.delete(notification.id);
        // un « tout marquer comme lu » passé entre-temps couvre déjà cette notification
        if (notification.id <= readAllUpToId) return;
        mutationVersion += 1;
        setState({
            items: state.items.map((item) => (item.id === notification.id ? { ...item, isRead: false } : item)),
            unreadCount: state.unreadCount + 1,
        });
    }
}

async function markAllNotificationsAsRead() {
    if (!state.userKey || (state.unreadCount === 0 && !state.items.some((item) => !item.isRead))) return;
    if (!canCall('read-all')) return;
    const currentGeneration = generation;
    markCall('read-all');
    const previousUnreadCount = state.unreadCount;
    const previousNewCount = state.newCount;
    const previousReadAllUpToId = readAllUpToId;
    const unreadIds = new Set(state.items.filter((item) => !item.isRead).map((item) => item.id));
    readAllUpToId = state.items.reduce((max, item) => Math.max(max, item.id), readAllUpToId);
    mutationVersion += 1;
    setState({
        items: state.items.map((item) => (item.isRead ? item : { ...item, isRead: true })),
        unreadCount: 0,
        // plus rien de non lu : plus rien de nouveau non plus
        newCount: 0,
    });
    try {
        await NotificationRepository.markAllAsRead();
    } catch {
        if (currentGeneration !== generation) return;
        readAllUpToId = previousReadAllUpToId;
        mutationVersion += 1;
        setState({
            items: state.items.map((item) => (
                unreadIds.has(item.id) && !readIds.has(item.id) ? { ...item, isRead: false } : item
            )),
            unreadCount: previousUnreadCount,
            newCount: previousNewCount,
        });
    }
}

/**
 * Ouverture du panneau : le badge retombe à zéro tout de suite, le repère « vu » est
 * enregistré côté serveur. Échec silencieux : le badge reviendra au prochain polling.
 */
async function markNotificationsSeen() {
    if (!state.userKey) return;
    if (!canCall('seen')) return;
    const currentGeneration = generation;
    markCall('seen');
    lastSeenAt = Date.now();
    mutationVersion += 1;
    if (state.newCount !== 0) setState({ newCount: 0 });
    try {
        await NotificationRepository.markSeen();
    } catch {
        if (currentGeneration !== generation) return;
        // l'appel a échoué : le prochain compteur serveur fait foi
        lastSeenAt = 0;
    }
}

/** Masquage optimiste : la notification disparaît, elle revient à sa place si l'appel échoue. */
async function hideNotification(notification: NotificationItem) {
    if (!state.userKey || hiddenIds.has(notification.id)) return;
    const key = `hide:${notification.id}`;
    if (!canCall(key)) return;
    const currentGeneration = generation;
    const currentFilters = filtersVersion;
    markCall(key);
    const index = state.items.findIndex((item) => item.id === notification.id);
    const wasUnread = index !== -1 ? !state.items[index].isRead : !notification.isRead;
    hiddenIds.add(notification.id);
    mutationVersion += 1;
    setState({
        items: state.items.filter((item) => item.id !== notification.id),
        total: Math.max(state.total - (index !== -1 ? 1 : 0), 0),
        unreadCount: wasUnread ? Math.max(state.unreadCount - 1, 0) : state.unreadCount,
    });
    try {
        await NotificationRepository.hide(notification.id);
    } catch {
        if (currentGeneration !== generation) return;
        hiddenIds.delete(notification.id);
        mutationVersion += 1;
        // toujours sur la même liste : on la remet à son rang d'origine
        if (currentFilters === filtersVersion && index !== -1 && !state.items.some((item) => item.id === notification.id)) {
            const items = [...state.items];
            items.splice(Math.min(index, items.length), 0, notification);
            setState({
                items,
                total: state.total + 1,
                unreadCount: wasUnread ? state.unreadCount + 1 : state.unreadCount,
            });
        } else if (wasUnread) {
            setState({ unreadCount: state.unreadCount + 1 });
        }
    }
}

/** Nouveaux filtres : la liste repart de la première page (les compteurs ne bougent pas). */
function setNotificationFilters(patch: Partial<NotificationFilters>) {
    const filters: NotificationFilters = { ...state.filters, ...patch };
    if (filters.category === state.filters.category && filters.unreadOnly === state.filters.unreadOnly) return;
    filtersVersion += 1;
    listFetchedAt = 0;
    setState({ filters, items: [], total: 0, page: 0, loaded: false, loading: false, loadingMore: false });
    if (state.userKey) fetchFirstPage(true);
}

/**
 * Notifications in-app de l'utilisateur connecté (prof ou client) : chargement initial,
 * polling des compteurs toutes les 60 s quand l'onglet est visible, arrêt en arrière-plan
 * et rafraîchissement au retour, back-off à 5 min après 3 échecs consécutifs.
 */
const useNotifications = (): UseNotificationsResult => {
    const currentUser = useSelector((rootState: any) => rootState?.user?.currentUser);
    const [firebaseUser] = useAuthState(auth);
    const account = currentUser?.isClient
        ? currentUser?.client
        : currentUser?.isTeachr ? currentUser?.teachr : undefined;
    const userKey: string | null = firebaseUser && currentUser?.isAuthenticated && account?.id
        ? `${currentUser.isClient ? 'client' : 'teachr'}:${account.id}`
        : null;

    const snapshot = useSyncExternalStore(subscribe, getSnapshot);

    useEffect(() => {
        if (!userKey) return;
        activate(userKey);
        return deactivate;
    }, [userKey]);

    // données d'un autre compte (avant la réinitialisation du store) : jamais affichées
    const current = userKey !== null && snapshot.userKey === userKey ? snapshot : INITIAL_STATE;

    return {
        ...current,
        enabled: userKey !== null,
        hasMore: current.items.length < current.total,
        refresh: refreshNotifications,
        loadMore: loadMoreNotifications,
        markAsRead: markNotificationAsRead,
        markAllAsRead: markAllNotificationsAsRead,
        markSeen: markNotificationsSeen,
        hide: hideNotification,
        setFilters: setNotificationFilters,
    };
};

export default useNotifications;
